import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useWhiteLabel } from "../../contexts/WhiteLabelContext";
import {
  WhiteLabelButton,
  useWhiteLabelStyles,
} from "../../utils/whiteLabelUtils";
import UserProfileDropdown from "./../../components/common/UserProfileDropdown";
import ApiService from "../../services/api";
import PatientHeader from "./PatientHeader";
import PatientFooter from "./PatientFooter";
import namer from "color-namer";

interface Product {
  product_id: number;
  name: string;
  description: string;
  final_price: number;
  features: string[];
  is_popular: boolean;
  is_active: boolean;
  created_at: string;
  image?: string;
}

interface ProductResponse {
  data: Product[];
  message: string;
  page: number;
  success: boolean;
  total: number;
  totalPages: number;
}

interface CartItem {
  product: Product;
  quantity: number;
}

const faqData = [
  {
    question: "Are there payment options?",
    answer:
      "Yes, we offer flexible payment options including credit/debit cards and bank transfers.",
  },
  {
    question: "Who do I talk to if I have questions?",
    answer:
      "Our dedicated support team is available to help you with any questions. You can reach out through email us at support@gattacagenomics.com, or schedule a consultation with one of our specialists. We also have a 24/7 helpline for urgent inquiries.",
  },
  {
    question: "What is your cancellation policy?",
    answer:
      "You can cancel your order up to 48 hours before the scheduled procedure without any penalty. For cancellations made within 48 hours, a small processing fee may apply. Emergency cancellations due to medical reasons are handled on a case-by-case basis with full refunds typically provided.",
  },
  {
    question: "Do I have to purchase everything now or can I do it later?",
    answer:
      "You have the flexibility to purchase services as needed. You can add items to your cart and complete the purchase later, or buy individual services as your treatment progresses.",
  },
  {
    question: "How do I change my account email?",
    answer:
      "For security reasons, you cannot change your email address yourself. If you need to update it, please contact our support team, and they will assist you with the change.",
  },
];

const PatientDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { contactLink, primaryColor, isWhiteLabel } = useWhiteLabel();
  const { getPrimaryButtonClasses, getButtonStyle } = useWhiteLabelStyles();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCartNotification, setShowCartNotification] = useState(false);
  const [purchasedProductIds, setPurchasedProductIds] = useState<number[]>([]);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const pageSize = 6; // Show 6 products per page

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  useEffect(() => {
    fetchProducts();
  }, [currentPage]);

  useEffect(() => {
    // Load cart from localStorage on component mount
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const data: ProductResponse = await ApiService.getPatientProducts(
        currentPage,
        pageSize
      );

      if (data.success) {
        setProducts(data.data);
        setTotalPages(data.totalPages);

        // Check if each product is already purchased
        data.data.forEach((product) => {
          checkIfProductPurchased(product.product_id);
        });
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToCart = (product: Product) => {
    const existingItem = cart.find(
      (item) => item.product.product_id === product.product_id
    );
    let updatedCart: CartItem[];

    if (existingItem) {
      // If product already in cart, increase quantity
      updatedCart = cart.map((item) =>
        item.product.product_id === product.product_id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      // Add new product to cart
      updatedCart = [...cart, { product, quantity: 1 }];
    }

    setCart(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));

    // Show notification
    setShowCartNotification(true);
    setTimeout(() => setShowCartNotification(false), 3000);
  };

  const handleOrderReceipt = async (product_id: number) => {
    try {
      const response = await ApiService.getPatientOrderReceipt(product_id);

      const blob = new Blob([response], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `order_receipt_${product_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading receipt:", error);
    }
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  function getColorName(hex: string) {
    const result = namer(hex);
    return result.basic[0].name; // e.g. "lime"
  }

  const handleCartClick = () => {
    navigate("/patient/checkout");
  };

  const checkIfProductPurchased = async (productId: number) => {
    try {
      const response = await ApiService.getOrderByProductID(productId);
      if (response?.data?.length) {
        const status = response.data[0].payment_status;
        if (["AUTHORISED", "CAPTURED", "APPROVED"].includes(status)) {
          setPurchasedProductIds((prev) => [...prev, productId]);
        }
      }
    } catch (error) {
      console.error("Error checking purchase status:", error);
    }
  };

  const handleViewDetails = (productId: number) => {
    console.log(`View details for product ${productId}`);
  };

  const isProductInCart = (productId: number) => {
    return cart.some((item) => item.product.product_id === productId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Cart Notification */}
      {showCartNotification && (
        <div className="fixed top-20 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          <div className="flex items-center">
            <svg
              className="h-5 w-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            Product added to cart!
          </div>
        </div>
      )}

      {/* Header */}
      <PatientHeader
        isNavigateCheckout={true}
        itemsCount={getCartItemCount()}
      />

      {/* Hero Section */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight">
            Clarity from Conception
            {/* <br />
            for genetic excellence */}
          </h1>
          <p className="mt-6 text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Evidence based insights that support informed decisions
          </p>

          {/* Consent Notice */}
          <div
            className={`mt-4 p-4 bg-${getColorName(
              primaryColor
            )}-50 rounded-lg border ${!isWhiteLabel ? "border-blue-200" : ""}`}
          >
            <p className="text-sm text-gray-700">
              To view the available consents, please{" "}
              <button
                onClick={() => navigate("/patient/consents")}
                className={`${
                  !isWhiteLabel ? "text-blue-600 hover:text-blue-700" : ""
                } underline font-medium`}
                style={isWhiteLabel ? { color: primaryColor } : {}}
              >
                click here
              </button>
            </p>
          </div>

          {/* <button className="mt-8 inline-flex items-center text-blue-600 hover:text-blue-700 font-medium">
            See items below
            <svg
              className="ml-2 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button> */}
        </div>
      </div>

      {/* Products Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div
              className="animate-spin rounded-full h-12 w-12 border-b-2"
              // className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"
              style={{ borderColor: primaryColor }}
            ></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((product) => (
                <div
                  key={product.product_id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col"
                >
                  {/* Product Image Placeholder */}
                  <div
                    className={
                      product?.image
                        ? "h-48 bg-gradient-to-br from-white-50 to-indigo-100 flex items-center justify-center relative overflow-hidden p-3"
                        : "h-48 bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center relative"
                    }
                  >
                    {product.is_popular && (
                      <span
                        className="absolute top-4 left-4 inline-block px-3 py-1 text-xs font-semibold rounded-full text-white z-10"
                        style={{ backgroundColor: primaryColor }}
                      >
                        Popular
                      </span>
                    )}
                    {product?.image ? (
                      // <div className="m-3">
                        <img
                          src={product?.image}
                          alt={product.name}
                          className="max-w-full max-h-full object-contain"
                        />
                      // </div>
                    ) : (
                      <div className="text-center">
                        <div className="h-16 w-16 mx-auto mb-3 bg-white rounded-2xl flex items-center justify-center shadow-sm">
                          <svg
                            className="h-8 w-8"
                            style={{ color: primaryColor }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {product.name}
                    </h3>

                    <p className="text-gray-600 mb-6 leading-relaxed line-clamp-3">
                      {product.description}
                    </p>

                    {/* Features - Fixed height container */}
                    <div className="flex-grow">
                      <ul className="space-y-3 mb-6">
                        {product.features.slice(0, 6).map((feature, index) => (
                          <li key={index} className="flex items-start">
                            <svg
                              className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            <span className="text-gray-600 text-sm leading-relaxed line-clamp-2">
                              {feature}
                            </span>
                          </li>
                        ))}
                        {/* Add placeholder items if less than 4 features */}
                        {Array.from({
                          length: Math.max(0, 4 - product.features.length),
                        }).map((_, index) => (
                          <li key={`placeholder-${index}`} className="h-6"></li>
                        ))}
                      </ul>
                    </div>

                    {/* Price */}
                    <div className="mb-6">
                      <p className="text-2xl font-bold text-gray-900">
                        ${product.final_price.toFixed(2)}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="space-y-3 mt-auto">
                      <WhiteLabelButton
                        onClick={() =>
                          purchasedProductIds.includes(product.product_id)
                            ? handleOrderReceipt(product.product_id)
                            : handleAddToCart(product)
                        }
                        disabled={
                          isProductInCart(product.product_id) &&
                          !purchasedProductIds.includes(product.product_id)
                        }
                        className={`w-full px-6 py-3 rounded-xl font-medium text-white ${
                          isProductInCart(product.product_id) &&
                          !purchasedProductIds.includes(product.product_id)
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        {purchasedProductIds.includes(product.product_id)
                          ? "Download receipt"
                          : isProductInCart(product.product_id)
                          ? "In Cart"
                          : "Add to cart"}
                      </WhiteLabelButton>
                      {/* <button
                        onClick={() => handleViewDetails(product.product_id)}
                        className="w-full border border-gray-300 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                      >
                        Chat to sales
                      </button> */}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-12 flex justify-center items-center space-x-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                <div className="flex space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-4 py-2 text-sm font-medium rounded-md ${
                          currentPage === page
                            ? "text-white"
                            : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                        }`}
                        // className={`px-4 py-2 text-sm font-medium rounded-md ${
                        //   currentPage === page
                        //     ? "bg-blue-600 text-white"
                        //     : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                        // }`}
                        style={
                          currentPage === page
                            ? {
                                backgroundColor: primaryColor,
                                borderColor: primaryColor,
                              }
                            : {}
                        }
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
          Frequently asked questions
        </h2>
        <p className="text-center text-gray-600 mb-12">
          Everything you need to know about the product and billing
        </p>

        <div className="space-y-4">
          {faqData.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl border border-gray-200 overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full text-left p-6 hover:border-gray-300 transition-colors flex justify-between items-center group"
              >
                <span className="text-gray-900 font-medium flex-1">
                  {faq.question}
                </span>
                <svg
                  className={`h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-all duration-200 ${
                    openFAQ === index ? "rotate-45" : ""
                  }`}
                  style={
                    openFAQ === index && isWhiteLabel
                      ? { color: primaryColor }
                      : {}
                  }
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
              </button>

              {/* Collapsible Answer */}
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  openFAQ === index
                    ? "max-h-96 opacity-100"
                    : "max-h-0 opacity-0"
                }`}
              >
                <div className="px-6 pb-6 pt-0">
                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="mt-16 bg-gray-50 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-3">
            Still have questions?
          </h3>
          <p className="text-gray-600 mb-8">
            Can't find the answer you're looking for? Please chat to our
            friendly team.
          </p>
          <WhiteLabelButton
            className="text-white px-8 py-3 rounded-xl font-medium"
            // className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 transition-colors font-medium"
            onClick={() =>
              window.open(contactLink, "_blank", "noopener,noreferrer")
            }
          >
            Get in touch
          </WhiteLabelButton>
        </div>
      </div>

      <PatientFooter />
    </div>
  );
};

export default PatientDashboard;
